import { useState, useMemo, useCallback } from 'react';

// ID to Question ID mapping (constant outside hook)
const idToQuestionId = {
  1: 'FIN-001', 2: 'FIN-002', 3: 'FIN-003', 4: 'FIN-004', 5: 'FIN-005',
  6: 'FIN-006', 7: 'FIN-007', 8: 'FIN-008', 9: 'FIN-009', 10: 'FIN-010',
  11: 'REV-001', 12: 'REV-002', 13: 'REV-003', 14: 'REV-004', 15: 'REV-005',
  16: 'REV-006', 17: 'REV-007', 18: 'REV-008', 19: 'REV-009', 20: 'CUST-001',
  21: 'CUST-002', 22: 'CUST-003', 23: 'CUST-004', 24: 'CUST-005', 25: 'CUST-006',
  26: 'CUST-007', 27: 'CUST-008', 28: 'MGT-001', 29: 'MGT-002', 30: 'MGT-003',
  31: 'MGT-004', 32: 'MGT-005', 33: 'MGT-006', 34: 'MGT-007', 35: 'MGT-008',
  36: 'MGT-009', 37: 'MGT-010', 38: 'COMP-001', 39: 'COMP-002', 40: 'COMP-003',
  41: 'COMP-004', 42: 'COMP-005', 43: 'COMP-006', 44: 'COMP-007', 45: 'COMP-008',
  46: 'GROW-001', 47: 'GROW-002', 48: 'GROW-003', 49: 'GROW-004', 50: 'GROW-005',
  51: 'GROW-006', 52: 'GROW-007', 53: 'GROW-008', 54: 'GROW-009', 55: 'IP-001',
  56: 'IP-002', 57: 'IP-003', 58: 'IP-004', 59: 'IP-005', 60: 'IP-006',
  61: 'IP-007', 62: 'IP-008', 63: 'LEGAL-001', 64: 'LEGAL-002', 65: 'LEGAL-003',
  66: 'LEGAL-004', 67: 'LEGAL-005', 68: 'LEGAL-006', 69: 'LEGAL-007', 70: 'LEGAL-008',
  71: 'LEGAL-009', 72: 'OWNER-001', 73: 'OWNER-002', 74: 'OWNER-003', 75: 'OWNER-004',
  76: 'OWNER-005', 77: 'OWNER-006', 78: 'OWNER-007', 79: 'OWNER-008', 80: 'OWNER-009',
  81: 'OWNER-010', 82: 'STRAT-001', 83: 'STRAT-002', 84: 'STRAT-003', 85: 'STRAT-004',
  86: 'STRAT-005', 87: 'STRAT-006', 88: 'STRAT-007', 89: 'STRAT-008'
};

// Helper function to convert numeric IDs to question_ids
const convertIdsToQuestionIds = (numericIds) => {
  return numericIds.map(id => idToQuestionId[id]);
};

/**
 * Custom hook for managing assessment filters
 * Handles gap zone, activity type, intangible asset, and readiness/attractiveness filtering
 */
const useAssessmentFilters = () => {
  // Filter state
  const [selectedGapFilter, setSelectedGapFilter] = useState(null);
  const [activityTypeFilter, setActivityTypeFilter] = useState(null);
  const [intangibleAssetFilter, setIntangibleAssetFilter] = useState(null);
  const [readinessAttractivenessFilter, setReadinessAttractivenessFilter] = useState(null);

  // Activity Type and Intangible Asset Type Mappings (memoized)
  const activityTypeMapping = useMemo(() => ({
    'De-Risking Activity': [
      ...convertIdsToQuestionIds([64, 73, 22, 67, 7, 59, 32, 76, 78, 70, 69, 62, 2, 56, 5, 57, 9, 27, 21, 65, 79, 11, 77, 72, 63, 68, 20, 15, 3, 33, 71, 66, 55, 13, 17]),
      // Family Enterprise Questions (De-Risking)
      'BOARD-003', 'BOARD-006', 'BOARD-007', 'BOUND-001', 'BOUND-004', 'BOUND-007',
      'EMP-001', 'EMP-004', 'EMP-006', 'EQUITY-001', 'EQUITY-002', 'EQUITY-003',
      'EQUITY-004', 'EQUITY-006', 'EQUITY-007', 'GOV-001', 'GOV-003', 'GOV-004',
      'GOV-006', 'RES-003', 'SUCC-001', 'SUCC-004', 'SUCC-005', 'WTRANS-001',
      'WTRANS-002', 'WTRANS-003', 'WTRANS-004', 'WTRANS-006', 'WTRANS-007'
    ],
    'Strategy Activity': [
      ...convertIdsToQuestionIds([88, 43, 40, 51, 84, 58, 82, 87, 49, 38, 41, 46, 60, 85, 44, 47, 89, 45, 50, 83, 42, 61, 53, 86, 39]),
      // Family Enterprise Questions
      'BOARD-002', 'BOARD-004', 'BOARD-005', 'BOUND-003', 'BOUND-008', 'DEV-003',
      'DEV-006', 'EQUITY-005', 'GOV-002', 'GOV-007', 'SUCC-002', 'SUCC-003',
      'SUCC-006', 'SUCC-008', 'VALUES-001', 'VALUES-002', 'VALUES-004', 'VALUES-005',
      'VALUES-006', 'VALUES-007', 'VALUES-008', 'WTRANS-005'
    ],
    'Efficiency Activity': [
      ...convertIdsToQuestionIds([74, 6, 18, 52, 81, 54, 48, 37, 80, 19, 14, 16, 12, 10, 34]),
      // Family Enterprise Questions
      'BOARD-001', 'BOARD-008', 'BOUND-002', 'EMP-002', 'EMP-003', 'EMP-005',
      'EMP-008', 'EQUITY-008', 'GOV-005', 'RES-002'
    ],
    'Growth Activity': [
      ...convertIdsToQuestionIds([4, 26, 24, 1, 8, 23, 25]),
      // Family Enterprise Questions
      'DEV-001', 'DEV-002', 'DEV-004', 'DEV-005', 'DEV-007', 'DEV-008',
      'EMP-007', 'SUCC-007'
    ],
    'Culture Activity': [
      ...convertIdsToQuestionIds([28, 30, 75, 35, 31, 36, 29]),
      // Family Enterprise Questions
      'BOUND-005', 'BOUND-006', 'GOV-008', 'RES-001', 'RES-004', 'RES-005',
      'RES-006', 'RES-007', 'RES-008', 'VALUES-003', 'WTRANS-008'
    ]
  }), []);

  const intangibleAssetMapping = useMemo(() => ({
    'Human': [
      ...convertIdsToQuestionIds([28, 30, 75, 33, 74, 35, 31, 80, 62, 76, 32, 29, 67]),
      // Family Enterprise Questions
      'BOARD-004', 'BOARD-006', 'DEV-001', 'DEV-002', 'DEV-003', 'DEV-004',
      'DEV-005', 'DEV-006', 'DEV-007', 'DEV-008', 'EMP-002', 'EMP-003',
      'EMP-004', 'EMP-006', 'EMP-007', 'EMP-008', 'GOV-008', 'SUCC-002',
      'SUCC-003', 'SUCC-004', 'SUCC-006', 'SUCC-007', 'SUCC-008', 'VALUES-002',
      'VALUES-003', 'VALUES-005', 'VALUES-006', 'VALUES-008'
    ],
    'Structural': [
      ...convertIdsToQuestionIds([55, 66, 58, 81, 54, 79, 48, 37, 60, 47, 19, 57, 14, 16, 5, 56, 50, 12, 83, 10, 69, 42, 36, 70, 61, 53, 59, 34, 7, 64]),
      // Family Enterprise Questions
      'BOARD-001', 'BOARD-002', 'BOARD-003', 'BOARD-005', 'BOARD-007', 'BOARD-008',
      'EMP-001', 'EMP-005', 'EQUITY-001', 'EQUITY-002', 'EQUITY-003', 'EQUITY-004',
      'EQUITY-006', 'EQUITY-007', 'GOV-001', 'GOV-002', 'GOV-004', 'GOV-005',
      'GOV-006', 'GOV-007', 'RES-002', 'SUCC-001', 'SUCC-005', 'VALUES-001',
      'VALUES-004', 'VALUES-007', 'WTRANS-001', 'WTRANS-002', 'WTRANS-004',
      'WTRANS-006', 'WTRANS-007'
    ],
    'Customer': convertIdsToQuestionIds([26, 15, 24, 20, 23, 21, 27, 78, 86, 25, 22, 73]),
    'Social': [
      ...convertIdsToQuestionIds([88, 43, 40, 84, 82, 87, 38, 41, 85, 44, 89, 45, 39]),
      // Family Enterprise Questions
      'BOUND-001', 'BOUND-002', 'BOUND-003', 'BOUND-004', 'BOUND-005', 'BOUND-006',
      'BOUND-007', 'GOV-003', 'RES-001', 'RES-003', 'RES-004', 'RES-005',
      'RES-006', 'RES-007', 'RES-008'
    ],
    'Revenue & Profitability': [
      ...convertIdsToQuestionIds([17, 13, 51, 4, 71, 3, 6, 1, 8, 68, 49, 63, 72, 18, 77, 52, 46, 11, 65, 9, 2]),
      // Family Enterprise Questions
      'BOUND-008', 'EQUITY-005', 'EQUITY-008', 'WTRANS-003', 'WTRANS-005', 'WTRANS-008'
    ]
  }), []);

  /**
   * Determines the gap zone based on score percentage
   * @param {number} score - Score percentage (0-100)
   * @returns {string} Gap zone identifier
   */
  const getGapZone = (score) => {
    // N/A questions (score 0) should not be in any gap zone
    if (score === 0) return 'n-a';

    // Score is 0-100 percentage, map to new scale
    if (score > 86) return 'no-gaps';              // >86% - Areas with no gaps (Score 5-6)
    if (score > 72) return 'minor-gaps';           // 72-86% - Areas with minor gaps (Score 4-5)
    if (score > 57) return 'considerable-gaps';    // 57-72% - Areas with considerable gaps (Score 3-4)
    if (score > 43) return 'critical-gaps';        // 43-57% - Areas with critical gaps (Score 2-3)
    if (score > 28) return 'very-critical-gaps';   // 28-43% - Areas with very critical gaps (Score 1-2)
    return 'extremely-critical';                   // >0 and <=28% - Areas with extremely critical gaps (Score 1)
  };

  /**
   * Gets gap zone information including label, color, and range
   * @param {string} zone - Gap zone identifier
   * @returns {object} Gap zone information
   */
  const getGapZoneInfo = (zone) => {
    const zones = {
      'no-gaps': { label: 'No Gaps', color: '#a07d5a', range: '>86%', scoreRange: '86-100%' },
      'minor-gaps': { label: 'Minor Gaps', color: '#6ba86e', range: '72-86%', scoreRange: '72-86%' },
      'considerable-gaps': { label: 'Considerable Gaps', color: '#6ca5d6', range: '57-72%', scoreRange: '57-72%' },
      'critical-gaps': { label: 'Critical Gaps', color: '#d4c563', range: '43-57%', scoreRange: '43-57%' },
      'very-critical-gaps': { label: 'Very Critical Gaps', color: '#d9a066', range: '28-43%', scoreRange: '28-43%' },
      'extremely-critical': { label: 'Extremely Critical', color: '#d97373', range: '0-28%', scoreRange: '0-28%' },
      'n-a': { label: 'N/A', color: '#9ca3af', range: 'N/A', scoreRange: 'N/A' }
    };
    return zones[zone];
  };

  /**
   * Filters questions based on active filters
   * @param {Array} questions - Array of questions to filter
   * @param {Function} getAnswer - Function to get answer for a question
   * @returns {Array} Filtered questions
   */
  const getFilteredQuestions = useCallback((questions, getAnswer) => {
    let filtered = [...questions];

    // Apply gap zone filter
    if (selectedGapFilter) {
      if (selectedGapFilter === 'n-a') {
        filtered = filtered.filter(q => {
          const answer = getAnswer(q.question_id);
          return answer && answer.answer_value === 0;
        });
      } else {
        filtered = filtered.filter(q => {
          const answer = getAnswer(q.question_id);
          if (!answer) return false;
          return getGapZone(answer.score) === selectedGapFilter;
        });
      }
    }

    // Apply activity type filter
    if (activityTypeFilter) {
      const activityQuestionIds = activityTypeMapping[activityTypeFilter] || [];
      filtered = filtered.filter(q => activityQuestionIds.includes(q.question_id));
    }

    // Apply intangible asset filter
    if (intangibleAssetFilter) {
      const assetQuestionIds = intangibleAssetMapping[intangibleAssetFilter] || [];
      filtered = filtered.filter(q => assetQuestionIds.includes(q.question_id));
    }

    // Apply readiness/attractiveness filter
    if (readinessAttractivenessFilter) {
      filtered = filtered.filter(q => {
        if (readinessAttractivenessFilter === 'Readiness') {
          return q.impacts_readiness === true;
        } else if (readinessAttractivenessFilter === 'Attractiveness') {
          return q.impacts_attractiveness === true;
        }
        return true;
      });
    }

    return filtered;
  }, [selectedGapFilter, activityTypeFilter, intangibleAssetFilter, readinessAttractivenessFilter, activityTypeMapping, intangibleAssetMapping]);

  /**
   * Checks if any filters are currently active
   * @returns {boolean} True if any filter is active
   */
  const hasActiveFilters = useCallback(() => {
    return !!(selectedGapFilter || activityTypeFilter || intangibleAssetFilter || readinessAttractivenessFilter);
  }, [selectedGapFilter, activityTypeFilter, intangibleAssetFilter, readinessAttractivenessFilter]);

  /**
   * Clears all active filters
   */
  const clearAllFilters = useCallback(() => {
    setSelectedGapFilter(null);
    setActivityTypeFilter(null);
    setIntangibleAssetFilter(null);
    setReadinessAttractivenessFilter(null);
  }, []);

  return {
    // Filter state
    selectedGapFilter,
    setSelectedGapFilter,
    activityTypeFilter,
    setActivityTypeFilter,
    intangibleAssetFilter,
    setIntangibleAssetFilter,
    readinessAttractivenessFilter,
    setReadinessAttractivenessFilter,

    // Filter mappings
    activityTypeMapping,
    intangibleAssetMapping,

    // Helper functions
    getGapZone,
    getGapZoneInfo,
    getFilteredQuestions,
    hasActiveFilters,
    clearAllFilters
  };
};

export default useAssessmentFilters;
